'use client'

import { productTemplateService } from '@/domain/ProductTemplate'
import { LoadingOverlay } from '@mantine/core'
import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import { useRouter } from 'next/router'
import React from 'react'
import { toast } from 'react-toastify'
import { useAccountContext } from './account-context'
import sleep from '@/utils/sleep'
import StockAlert from 'stoux-shared-types/models/stock-alert'

interface StockAlertContext {
    addAlert: (templateId: string) => void
    removeAlert: (templateId: string) => void
    getIsAlert: (templateId: string) => boolean
    stockAlerts: StockAlert[]
    count: number
}

const StockAlertContext = React.createContext<StockAlertContext | null>(null)

export const useStockAlerts = () => {
    const context = React.useContext(StockAlertContext)
    if (context === null) {
        throw new Error('useFavorites must be used within a FavoriteProvider')
    }
    return context
}

interface FavoriteProps {
    children: React.ReactNode
}

const IS_SERVER = typeof window === 'undefined'
const FAKE_UI_DELAY = 300 // ms

function useCreateStockAlert() {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (templateId: string) =>
            productTemplateService.createStockAlert(templateId),

        onMutate: async () => {
            await sleep(FAKE_UI_DELAY)
        },

        onSuccess: async () => {
            await queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'stock-alerts',
            })
        },

        onError: (err) => {
            if (axios.isAxiosError(err)) {
                toast.error(err.response?.data.error)
                return
            }
            toast.error(
                "Une erreur s'est produite lors de la création de l'alerte.",
            )
        },
    })

    return mutation
}

function useDeleteStockAlert() {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (id: string) => productTemplateService.deleteStockAlert(id),

        onMutate: async () => {
            await sleep(FAKE_UI_DELAY)
        },

        onSuccess: async () => {
            await queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'stock-alerts',
            })
        },

        onError: () => {
            toast.error(
                "Une erreur s'est produite lors de la suppression de l'alerte.",
            )
        },
    })

    return mutation
}

export function useListStockAlerts(customerId: string) {
    const query = useQuery({
        queryKey: ['stock-alerts', customerId],
        queryFn: () => productTemplateService.listStockAlerts(),
        enabled: !!customerId,
        placeholderData: keepPreviousData,
    })

    const stockAlerts = query.data?.alerts || []
    const count = stockAlerts?.length || 0

    return {
        ...query,
        stockAlerts,
        count,
    }
}

export const StockAlertsProvider = ({ children }: FavoriteProps) => {
    const { customer, refetchCustomer } = useAccountContext()
    const router = useRouter()

    const createAlert = useCreateStockAlert()
    const deleteAlert = useDeleteStockAlert()

    const isMutating = createAlert.isPending || deleteAlert.isPending

    const { stockAlerts, count } = useListStockAlerts(customer?.id || '')

    const addAlert = async (templateId: string) => {
        await refetchCustomer()
        if (!customer || !customer.id) {
            const { asPath } = router
            router.push(`/sign-in?redirect=${asPath}`)
            return
        }
        await createAlert.mutateAsync(templateId)
    }

    const removeAlert = async (templateId: string) => {
        await refetchCustomer()
        if (!customer || !customer.id) {
            const { asPath } = router
            router.push(`/sign-in?redirect=${asPath}`)
            return
        }

        const foundAlert = stockAlerts?.find(
            (favorite) => favorite.template.id === templateId,
        )

        if (!foundAlert) return
        await deleteAlert.mutateAsync(foundAlert?.id)
    }

    const getIsAlert = (templateId: string) => {
        if (!stockAlerts || !stockAlerts?.length) return false

        return stockAlerts?.some(
            (favorite) => favorite.template.id === templateId,
        )
    }

    return (
        <StockAlertContext.Provider
            value={{
                stockAlerts,
                count,
                addAlert,
                removeAlert,
                getIsAlert,
            }}
        >
            <LoadingOverlay visible={isMutating} />
            {children}
        </StockAlertContext.Provider>
    )
}
